/* eslint-disable react/prop-types */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Mousetrap from 'mousetrap';
import { nanoid } from 'nanoid';

import { TableVirtuoso } from 'react-virtuoso';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import CopyToClipboard from '../CopyToClipboard';
import Loader from '../../components/Loader';
import LoaderSkeleton from '../LoaderSkeleton';
import OverlayTriggerTooltip from '../OverlayTriggerTooltip';

import { useScrollSync, useOutsideClick } from '../../helpers';

import SortIndicator from '../SortIndicator';

import {
  copySelectedCellValues,
  getCopyTableValue,
  getCellDown,
  getCellLeft,
  getCellRight,
  getCellUp,
  getSelectedCellRange,
  sortTypes,
  transformColumns,
} from './utilities/helpers';

import styles from './_index.module.scss';

const tableId = nanoid();

const TableDataV2 = ({
  actionButtons = [],
  columns,
  data,
  isLoading,
  isUpdating,
  renderEmptyMessage,
  rowHeight = 30,
  setDownloadData,
  shouldTruncateHeaders = true,
  size,
  stickyHeader = false,
}) => {
  const tableRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const [sorting, setSorting] = useState([]);

  // Transform columns from react-table v7 format to TanStack v8 format
  const transformedColumns = useMemo(() => transformColumns(columns), [columns]);

  const table = useReactTable({
    columns: transformedColumns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    sortingFns: sortTypes,
    state: { sorting },
  });
  const [selectedCell, setSelectedCell] = useState(null); // for range selection
  const [rangeEndCell, setRangeEndCell] = useState(null);

  const clearSelectedCells = () => {
    setSelectedCell(null);
    setRangeEndCell(null);
  };

  useEffect(() => {
    // keyboard select
    if (selectedCell) {
      Mousetrap.bind(['up'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellUp({ selectedCell, table }));
      });
      Mousetrap.bind(['down'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellDown({ selectedCell, table }));
      });
      Mousetrap.bind(['left'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellLeft({ selectedCell, table }));
      });
      Mousetrap.bind(['right'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellRight({ selectedCell, table }));
      });
    }
  }, [selectedCell]);

  useEffect(() => {
    // copy-to-clipboard
    if (selectedCell || rangeEndCell) {
      Mousetrap.bind(['ctrl+c', 'command+c'], () => {
        copySelectedCellValues({ rangeEndCell, selectedCell, table });
      });
      return () => {
        Mousetrap.unbind(['ctrl+c', 'command+c']);
      };
    }
  }, [selectedCell, rangeEndCell, table]);

  useEffect(() => {
    // when the sort changes, cell selection is cleared and updates the download data with the sorted table data (only for Shopper Metrics .ppt)
    clearSelectedCells();
    if (setDownloadData) {
      setDownloadData((prevData) => ({
        ...prevData,
        table: {
          columns,
          data: table.getRowModel().rows.map((row) => row.original),
        },
      }));
    }
  }, [sorting, columns, setDownloadData]);

  useOutsideClick({
    onCallback: () => {
      clearSelectedCells();
    },
    ref: tableRef,
  });

  useEffect(() => {
    if (!stickyHeader) return;

    const headerEl = stickyHeaderRef.current;
    const tableEl = tableRef.current;

    if (!headerEl || !tableEl) return;

    const handleScroll = () => {
      headerEl.scrollLeft = tableEl.scrollLeft;
    };

    tableEl.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      tableEl.removeEventListener('scroll', handleScroll);
    };
  }, [stickyHeader]);

  const calculatedHeight = useMemo(
    () => table.getCoreRowModel().rows.length * rowHeight,
    [table, rowHeight]
  );

  const selectedRange = useMemo(
    () =>
      getSelectedCellRange({
        endCell: rangeEndCell ?? selectedCell,
        startCell: selectedCell,
        table,
      }),
    [rangeEndCell, selectedCell, table]
  );

  return (
    <div className={styles['table-data']}>
      <div className={styles['table-data-actions']}>
        {actionButtons?.length > 0 && actionButtons}
        <CopyToClipboard
          text="Copy Table"
          value={getCopyTableValue({
            columns: transformedColumns,
            data: data,
          })}
        />
      </div>
      {isLoading ? (
        <>
          <div className={styles['table-data-thead']}>
            <TableHeader table={table} />
          </div>
          <div>
            {[...Array(size)].map((_, index) => (
              <div
                className={classNames(styles['table-data-tr'], styles['is-loading'])}
                key={`row-${index}`}
              >
                <LoaderSkeleton height={19.5} width={1280}>
                  <rect height="19.5" rx="2" ry="2" width="100%" x="0" y="0" />
                </LoaderSkeleton>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {stickyHeader ? (
            <div className={styles['table-data-header-container']} ref={stickyHeaderRef}>
              <TableHeader shouldTruncateHeaders={shouldTruncateHeaders} table={table} />
            </div>
          ) : null}
          <TableVirtuoso
            components={COMPONENTS}
            context={{ table, tableRef }}
            data={data}
            fixedFooterContent={() => <TableFooter table={table} />}
            fixedHeaderContent={
              !stickyHeader
                ? () => <TableHeader shouldTruncateHeaders={shouldTruncateHeaders} table={table} />
                : undefined
            }
            itemContent={(index) => {
              let row = table.getRowModel().rows[index];
              row.index = index;

              return row.getVisibleCells().map((cell) => {
                const gradientBackgroundColor = cell.column.columnDef.gradient
                  ? `rgba(0, 227, 107, ${cell.getValue() / 2})`
                  : null;
                let highlight = false;

                if (typeof cell.column.columnDef.getCellHighlight === 'function') {
                  highlight = cell.column.columnDef.getCellHighlight({
                    rowIndex: row.index,
                    value: cell.getValue(),
                  });
                }

                return (
                  <div
                    className={classNames(styles['table-data-td'], {
                      [styles['is-active']]: selectedCell?.id === cell?.id,
                      [styles['is-selected']]: selectedRange[cell?.id],
                      [styles[`is-${highlight}`]]: highlight,
                    })}
                    key={cell.id}
                    onMouseDown={() => {
                      setSelectedCell(cell);
                      setRangeEndCell(cell);
                    }}
                    onMouseEnter={(e) => {
                      if (e.buttons === 1 && selectedCell) {
                        setRangeEndCell(cell);
                      }
                    }}
                    style={{
                      backgroundColor: gradientBackgroundColor,
                      boxSizing: 'border-box',
                      flex: `${cell.column.getSize() ?? 120} 0 auto`,
                      width: cell.column.getSize(),
                    }}
                  >
                    <p>{flexRender(cell.column.columnDef.cell, cell.getContext())}</p>
                  </div>
                );
              });
            }}
            style={{ minHeight: `${calculatedHeight}px` }}
            totalCount={table.getCoreRowModel().rows.length}
            useWindowScroll
          />
        </>
      )}
      {isUpdating && (
        <div className={styles['table-data-loader']}>
          <Loader />
        </div>
      )}
      {!isLoading && !isUpdating && table.getCoreRowModel().rows.length === 0 && (
        <div className={styles['table-data-empty-message']}>{renderEmptyMessage()}</div>
      )}
    </div>
  );
};

const TableFooter = ({ table }) => {
  if (!table) return null;

  const footerGroups = table.getFooterGroups();
  const hasAnyFooter = table
    .getAllColumns()
    .some((col) => col.columnDef.footer !== undefined && col.columnDef.footer !== null);
  if (!hasAnyFooter) return null;

  return (
    <>
      {footerGroups.map((footerGroup) => (
        <div className={styles['table-data-tr']} key={footerGroup.id}>
          {footerGroup.headers.map((header, index) => (
            <div
              className={styles['table-data-tf']}
              key={header.id}
              style={{
                boxSizing: 'border-box',
                flex: index === 0 ? '0 0 250px' : `${header.column.getSize() ?? 120} 0 auto`,
                width: index === 0 ? '250px' : header.column.getSize(),
              }}
            >
              {flexRender(header.column.columnDef.footer, header.getContext())}
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

const TableHeader = ({ shouldTruncateHeaders, table }) => {
  if (!table?.getHeaderGroups) return null;

  const headerGroups = table.getHeaderGroups();
  if (!headerGroups?.length) return null;

  return (
    <div className={styles['table-data-thead']}>
      {headerGroups.map((headerGroup) => (
        <div className={styles['table-data-tr']} key={headerGroup.id}>
          {headerGroup.headers?.map((header) => (
            <div
              aria-sort={
                header.column.getIsSorted() === 'desc'
                  ? 'descending'
                  : header.column.getIsSorted() === 'asc'
                    ? 'ascending'
                    : 'none'
              }
              className={classNames(styles['table-data-th'], header.column.className, {
                [styles['is-sortable']]: header.column?.getCanSort(),
              })}
              key={header.id}
              onClick={header.column.getToggleSortingHandler()}
              role="columnheader"
              style={{
                boxSizing: 'border-box',
                flex: `${header.column.getSize() ?? 120} 0 auto`,
                width: header.column.getSize(),
              }}
              tabIndex={header.column?.getCanSort() ? 0 : -1}
              title={flexRender(header.column.columnDef.header, header.getContext())}
            >
              <p
                className={classNames({
                  [styles['table-data-wrap-header']]: !shouldTruncateHeaders,
                })}
              >
                <OverlayTriggerTooltip
                  content={header.column.columnDef.headerTooltip}
                  placement="top"
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.index === 0 && ` (${table.getRowModel().rows.length})`}
                </OverlayTriggerTooltip>
              </p>
              <SortIndicator
                direction={header.column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
                isActive={!!header.column.getIsSorted()}
                isDisabled={!header.column.getCanSort()}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const COMPONENTS = {
  FillerRow: ({ height }) => (
    <div>
      <div style={{ height }}></div>
    </div>
  ),
  Table: ({ context: { tableRef }, ...props }) => (
    <div className={styles['table-data-table']} {...props} ref={tableRef} />
  ),
  TableBody: forwardRef((props, ref) => {
    const tbody = useScrollSync(tableId);
    useImperativeHandle(ref, () => tbody.current);
    return <div className={styles['table-data-tbody']} {...props} ref={tbody} />;
  }),
  TableFoot: forwardRef((props, ref) => (
    <div className={styles['table-data-tfoot']} {...props} ref={ref} />
  )),
  TableHead: forwardRef((props, ref) => {
    const thead = useScrollSync(tableId);
    useImperativeHandle(ref, () => thead.current);
    return <div className={styles['table-data-thead']} {...props} ref={thead} />;
  }),

  TableRow: ({ context: { table }, item, ...props }) => {
    const index = props['data-index'];
    const row = table.getRowModel().rows[index];
    const isHighlighted = row?.original?.row_variant === 'highlight';

    return (
      <div
        className={classNames(styles['table-data-tr'], {
          [styles['is-highlighted']]: isHighlighted,
        })}
        {...props}
        key={item.id}
      />
    );
  },
};

TableDataV2.defaultProps = {
  renderEmptyMessage: () => {},
  size: 15,
};

TableDataV2.propTypes = {
  actionButtons: PropTypes.arrayOf(PropTypes.node),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string,
      Cell: PropTypes.func,
      disableSortBy: PropTypes.bool,
      format: PropTypes.string,
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]),
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  isUpdating: PropTypes.bool,
  renderEmptyMessage: PropTypes.func,
  rowHeight: PropTypes.number,
  setDownloadData: PropTypes.func,
  shouldTruncateHeaders: PropTypes.bool,
  size: PropTypes.number,
  stickyHeader: PropTypes.bool,
};

export default TableDataV2;
